import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { 
  ArrowLeft, Star, Clock, Video, Phone, MessageSquare, 
  CheckCircle, Calendar, Users, GraduationCap, Award,
  IndianRupee, Shield, Sparkles, ChevronRight
} from 'lucide-react';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExpertCounseling = () => {
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    mobile: '',
    jee_rank: '',
    category: 'General',
    questions: ''
  });

  // Default experts data
  const defaultExperts = [
    {
      id: 'expert_1',
      name: 'Dr. Rajesh Kumar',
      title: 'Senior JEE Counselor',
      experience: '15+ years',
      specialization: ['IIT Selection', 'Branch Guidance', 'Career Planning'],
      rating: 4.9,
      reviews: 234,
      sessions_completed: 1500,
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200',
      bio: 'Former IIT Delhi professor with expertise in guiding students to top IITs and NITs.',
      languages: ['Hindi', 'English'],
      availability: 'Mon-Sat'
    },
    {
      id: 'expert_2',
      name: 'Priya Sharma',
      title: 'College Admission Expert',
      experience: '10+ years',
      specialization: ['NIT Admissions', 'State Quota', 'Fee Structure'],
      rating: 4.8,
      reviews: 189,
      sessions_completed: 1200,
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200',
      bio: 'Helped 1000+ students secure admissions in top engineering colleges across India.',
      languages: ['Hindi', 'English', 'Tamil'],
      availability: 'Mon-Fri'
    },
    {
      id: 'expert_3',
      name: 'Amit Verma',
      title: 'IIT Alumni Mentor',
      experience: '8+ years',
      specialization: ['IIT Life', 'Placements', 'Higher Studies'],
      rating: 4.7,
      reviews: 156,
      sessions_completed: 800,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
      bio: 'IIT Bombay alumnus now working at Google. Guides students on college life and career.',
      languages: ['Hindi', 'English'],
      availability: 'Weekends'
    }
  ];

  // Default packages
  const defaultPackages = [
    {
      id: 'pkg_basic',
      name: 'Quick Consultation',
      duration: 15,
      price: 299,
      original_price: 499,
      type: 'video',
      features: [
        'College shortlisting',
        'Basic branch guidance',
        'Q&A session'
      ],
      popular: false
    },
    {
      id: 'pkg_standard',
      name: 'Standard Session',
      duration: 30,
      price: 599,
      original_price: 999,
      type: 'video',
      features: [
        'Detailed college analysis',
        'Branch vs College strategy',
        'Cutoff predictions',
        'Choice filling guidance',
        'Email follow-up'
      ],
      popular: true
    },
    {
      id: 'pkg_premium',
      name: 'Premium Mentorship',
      duration: 60,
      price: 1499,
      original_price: 2499,
      type: 'video',
      features: [
        'Complete counseling roadmap',
        'Personalized college list',
        'All rounds guidance',
        'Document verification help',
        'Unlimited chat support (7 days)',
        'Choice filling assistance'
      ],
      popular: false
    },
    {
      id: 'pkg_complete',
      name: 'Complete Admission Support',
      duration: 'Multiple',
      price: 4999,
      original_price: 7999,
      type: 'package',
      features: [
        '3 video sessions (60 min each)',
        'End-to-end admission support',
        'All counseling rounds coverage',
        'Document preparation',
        'Seat confirmation guidance',
        'Priority WhatsApp support',
        '100% satisfaction guarantee'
      ],
      popular: false
    }
  ];

  useEffect(() => {
    fetchData();
    loadUserData();
  }, []);

  const loadUserData = () => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('userEmail');
    const userMobile = localStorage.getItem('userMobile');
    
    setBookingForm(prev => ({
      ...prev,
      name: userName || '',
      email: userEmail || '',
      mobile: userMobile || ''
    }));
  };

  const fetchData = async () => {
    try {
      const [expertsRes, packagesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/counseling/experts`),
        fetch(`${BACKEND_URL}/api/counseling/packages`)
      ]);
      
      const expertsData = await expertsRes.json();
      const packagesData = await packagesRes.json();
      
      setExperts(expertsData.experts?.length > 0 ? expertsData.experts : defaultExperts);
      setPackages(packagesData.packages?.length > 0 ? packagesData.packages : defaultPackages);
    } catch (error) {
      console.log('Using default data');
      setExperts(defaultExperts);
      setPackages(defaultPackages);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (expertId, date) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/counseling/slots?expert_id=${expertId}&date=${date}`);
      const data = await res.json();
      setAvailableSlots(data.slots || generateDefaultSlots());
    } catch (error) {
      setAvailableSlots(generateDefaultSlots());
    }
  };

  const generateDefaultSlots = () => {
    return [
      '10:00 AM', '11:00 AM', '12:00 PM',
      '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
      '6:00 PM', '7:00 PM', '8:00 PM'
    ];
  };

  const handleBookNow = (expert, pkg) => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      toast({
        title: "Almost there! Just one quick step",
        description: "Sign in to unlock exclusive access to our expert IIT/NIT counselors and personalized guidance",
        variant: "default"
      });
      navigate('/login');
      return;
    }
    
    setSelectedExpert(expert);
    setSelectedPackage(pkg);
    setShowBookingModal(true);
    
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    fetchAvailableSlots(expert.id, dateStr);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot('');
    if (selectedExpert) {
      fetchAvailableSlots(selectedExpert.id, date);
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot) {
      toast({ title: "Please select a time slot", variant: "destructive" });
      return;
    }
    if (!bookingForm.name || !bookingForm.mobile) {
      toast({ title: "Please fill all required fields", variant: "destructive" });
      return;
    }

    setBookingLoading(true);
    
    try {
      const userId = localStorage.getItem('userId');
      const bookingData = {
        user_id: userId,
        expert_id: selectedExpert.id,
        package_id: selectedPackage.id,
        date: selectedDate,
        time_slot: selectedSlot,
        user_name: bookingForm.name,
        user_email: bookingForm.email,
        user_mobile: bookingForm.mobile,
        jee_rank: bookingForm.jee_rank,
        category: bookingForm.category,
        questions: bookingForm.questions,
        amount: selectedPackage.price
      };

      const res = await fetch(`${BACKEND_URL}/api/counseling/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        if (data.payment_url) {
          // Redirect to payment
          window.location.href = data.payment_url;
        } else {
          // Mock payment success
          toast({
            title: "Booking Confirmed! 🎉",
            description: `Your session with ${selectedExpert.name} is booked for ${selectedDate} at ${selectedSlot}`,
          });
          setShowBookingModal(false);
          navigate('/dashboard');
        }
      } else {
        toast({ title: "Booking failed", description: data.error, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to book session", variant: "destructive" });
    } finally {
      setBookingLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white py-16">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
          
          <div className="max-w-3xl">
            <Badge className="bg-yellow-400 text-yellow-900 mb-4">
              <Sparkles className="w-3 h-3 mr-1" /> Expert Guidance
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get Expert JEE Counseling
            </h1>
            <p className="text-xl text-white/90 mb-6">
              Book 1-on-1 video sessions with IIT alumni and experienced counselors. 
              Get personalized college recommendations and admission guidance.
            </p>
            
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>1500+ Students Helped</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span>4.8+ Average Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span>100% Satisfaction Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Packages Section */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-2">Choose Your Plan</h2>
        <p className="text-slate-600 text-center mb-8">Select a package that fits your needs</p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {packages.map((pkg) => (
            <Card 
              key={pkg.id} 
              className={`relative overflow-hidden transition-all hover:shadow-xl ${
                pkg.popular ? 'border-2 border-purple-500 shadow-lg' : ''
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-3 py-1 rounded-bl-lg">
                  Most Popular
                </div>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <CardDescription>
                  {typeof pkg.duration === 'number' ? `${pkg.duration} minutes` : pkg.duration} session
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">₹{pkg.price}</span>
                    <span className="text-slate-400 line-through">₹{pkg.original_price}</span>
                  </div>
                  <Badge variant="secondary" className="mt-1 bg-green-100 text-green-700">
                    {Math.round((1 - pkg.price/pkg.original_price) * 100)}% OFF
                  </Badge>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full ${pkg.popular ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                  onClick={() => handleBookNow(experts[0], pkg)}
                >
                  Book Now <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Experts Section */}
        <h2 className="text-3xl font-bold text-center mb-2">Meet Our Experts</h2>
        <p className="text-slate-600 text-center mb-8">Experienced counselors ready to guide you</p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {experts.map((expert) => (
            <Card key={expert.id} className="overflow-hidden hover:shadow-lg transition-all">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-2" />
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <img 
                    src={expert.image} 
                    alt={expert.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  <div>
                    <h3 className="font-bold text-lg">{expert.name}</h3>
                    <p className="text-sm text-slate-600">{expert.title}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-medium">{expert.rating}</span>
                      <span className="text-slate-400">({expert.reviews} reviews)</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mt-4">{expert.bio}</p>
                
                <div className="flex flex-wrap gap-2 mt-4">
                  {expert.specialization.map((spec, idx) => (
                    <Badge key={idx} variant="secondary">{spec}</Badge>
                  ))}
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">{expert.sessions_completed}+</span> sessions
                  </div>
                  <div className="text-sm text-slate-600">
                    {expert.experience} experience
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-4"
                  onClick={() => handleBookNow(expert, packages.find(p => p.popular) || packages[1])}
                >
                  <Video className="w-4 h-4 mr-2" /> Book Session
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 bg-slate-50 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-center mb-6">Why Students Trust Us</h3>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="font-bold text-2xl">5000+</div>
              <div className="text-sm text-slate-600">Students Counseled</div>
            </div>
            <div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <GraduationCap className="w-6 h-6 text-green-600" />
              </div>
              <div className="font-bold text-2xl">95%</div>
              <div className="text-sm text-slate-600">Got Desired College</div>
            </div>
            <div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div className="font-bold text-2xl">IIT/NIT</div>
              <div className="text-sm text-slate-600">Alumni Mentors</div>
            </div>
            <div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="font-bold text-2xl">100%</div>
              <div className="text-sm text-slate-600">Money Back Guarantee</div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Your Session</DialogTitle>
            <DialogDescription>
              {selectedExpert && selectedPackage && (
                <span>
                  {selectedPackage.name} with {selectedExpert.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Package Summary */}
            {selectedPackage && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium">{selectedPackage.name}</div>
                    <div className="text-sm text-slate-600">
                      {typeof selectedPackage.duration === 'number' 
                        ? `${selectedPackage.duration} min video call` 
                        : selectedPackage.duration}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">₹{selectedPackage.price}</div>
                    <div className="text-sm text-slate-400 line-through">₹{selectedPackage.original_price}</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Date Selection */}
            <div>
              <Label>Select Date *</Label>
              <Input 
                type="date" 
                value={selectedDate}
                min={getMinDate()}
                max={getMaxDate()}
                onChange={(e) => handleDateChange(e.target.value)}
              />
            </div>
            
            {/* Time Slot Selection */}
            <div>
              <Label>Select Time Slot *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {availableSlots.map((slot) => (
                  <Button
                    key={slot}
                    type="button"
                    variant={selectedSlot === slot ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedSlot(slot)}
                    className="text-xs"
                  >
                    {slot}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* User Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input 
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input 
                  value={bookingForm.mobile}
                  onChange={(e) => setBookingForm({...bookingForm, mobile: e.target.value})}
                  placeholder="10-digit mobile"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={bookingForm.email}
                onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>JEE Rank (if available)</Label>
                <Input 
                  type="number"
                  value={bookingForm.jee_rank}
                  onChange={(e) => setBookingForm({...bookingForm, jee_rank: e.target.value})}
                  placeholder="Enter rank"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={bookingForm.category}
                  onValueChange={(v) => setBookingForm({...bookingForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['General', 'OBC', 'SC', 'ST', 'EWS'].map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Your Questions (Optional)</Label>
              <Textarea 
                value={bookingForm.questions}
                onChange={(e) => setBookingForm({...bookingForm, questions: e.target.value})}
                placeholder="What would you like to discuss in the session?"
                rows={3}
              />
            </div>
            
            {/* Payment Summary */}
            <div className="border-t pt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Session Fee</span>
                <span>₹{selectedPackage?.original_price}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600 mb-2">
                <span>Discount</span>
                <span>-₹{selectedPackage ? selectedPackage.original_price - selectedPackage.price : 0}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span>₹{selectedPackage?.price}</span>
              </div>
            </div>
            
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={handleConfirmBooking}
              disabled={bookingLoading || !selectedSlot}
            >
              {bookingLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span> Processing...
                </span>
              ) : (
                <>
                  <IndianRupee className="w-4 h-4 mr-1" />
                  Pay ₹{selectedPackage?.price} & Confirm
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              🔒 Secure payment • 100% refund if not satisfied
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ExpertCounseling;
